// ErrorBoundary.tsx
import React from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends React.Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return <ErrorFallback error={this.state.error} />;
    }

    return this.props.children;
  }
}

// ErrorFallback.tsx
interface ErrorFallbackProps {
  error?: Error;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({ error }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isChunkLoadError = React.useMemo(() => {
    if (!error) return false;

    const chunkFailedMessage = /Loading chunk/i.test(error.message);
    const cssChunkFailedMessage = /Loading CSS chunk/i.test(error.message);
    const dynamicImportError =
      /Failed to fetch dynamically imported module/i.test(error.message);
    const mimeTypeError =
      /Expected a JavaScript-or-Wasm module script but the server responded with a MIME type of "text\/html"/i.test(
        error.message
      );

    return (
      chunkFailedMessage ||
      cssChunkFailedMessage ||
      dynamicImportError ||
      mimeTypeError
    );
  }, [error]);
  const handleRetry = () => {
    // Clear any cached chunks and force a full page reload
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          caches.delete(name);
        });
        (window as any).location.reload();
      });
    } else {
      (window as any).location.reload();
    }
  };

  React.useEffect(() => {
    // If the error is related to Firebase context, redirect to login
    if (
      error?.message.includes(
        'useFirebase must be used within a FirebaseProvider'
      )
    ) {
      navigate('/login');
    }

    // Auto-retry chunk loading errors after a short delay
    if (isChunkLoadError) {
      const timer = setTimeout(() => {
        handleRetry();
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [error, navigate, isChunkLoadError]); // Show loading state while redirecting
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="p-8 text-center max-w-2xl">
        {isChunkLoadError ? (
          <div>
            <h2 className="text-2xl font-semibold mb-4">
              {t('ErrorBoundry.Updating', 'App is updating...')}
            </h2>
            <p className="text-gray-600 mb-6">
              {t(
                'ErrorBoundry.UpdateMessage',
                'We are refreshing the app to load the latest version. This will only take a moment.'
              )}
            </p>
            <div className="flex items-center justify-center mb-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
            <button
              onClick={handleRetry}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              {t('ErrorBoundry.RetryNow', 'Retry Now')}
            </button>
          </div>
        ) : (
          <div>
            <h2 className="text-2xl font-semibold mb-4">
              {t('ErrorBoundry.Message')}
            </h2>

            {/* Display the exact error */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-left">
                <h3 className="text-lg font-medium text-red-800 mb-2">
                  Error Details:
                </h3>
                <p className="text-red-700 font-mono text-sm mb-2">
                  <strong>Message:</strong> {error.message}
                </p>
                {error.stack && (
                  <div className="text-red-600 font-mono text-xs">
                    <strong>Stack Trace:</strong>
                    <pre className="mt-1 whitespace-pre-wrap overflow-auto max-h-40">
                      {error.stack}
                    </pre>
                  </div>
                )}
              </div>
            )}

            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ErrorBoundary;
